import Layout from "@/components/layout/Layout";
import React, { useState } from "react";
import Sidepanel from "@/components/layout/Sidepanel";
import Content from "@/components/layout/Content";
import ContentOptions from "@/components/layout/ContentOptions";
import Heading from "@/components/elements/Heading";
import NavigationButtons from "@/components/elements/Buttons";
import Label from "@/components/elements/Label";
import InputList from "@/components/layout/InputList";
import TextArea from "@/components/elements/TextArea";
import { Inertia } from "@inertiajs/inertia";
import api from "@/services/api";

/**
 * Input (Input/Entry) Component
 *
 * Final product configuration step where users specify detailed product variations
 * including dimensions, quantities, and descriptions. Handles comprehensive validation
 * of all input data and manages the transition to the shopping cart.
 *
 * @param {Object} props - Component props
 * @param {Object} props.session - Current session information
 * @param {string} props.shoppingurl - URL for shopping cart navigation
 * @param {string} props.step - Current configuration step identifier
 * @param {Array} props.steps - Available configuration steps
 * @param {Object} props.product - Product configuration data
 * @param {Object} props.product.properties - Product properties
 * @param {string} props.heading - Page heading text
 * @param {string} props.subheading - Page subheading text
 * @param {Array} props.variationoptions - Initial product variations
 * @param {string} props.uploadExcel - API endpoint for Excel file upload
 * @param {string} props.saveUrl - API endpoint for saving configuration
 * @param {string} props.createUrl - API endpoint for creating new variations
 * @param {string} props.prevLink - URL for previous step navigation
 * @param {string} props.nextLink - URL for next step navigation
 * @param {string} props.saveVariationUrl - API endpoint for saving individual variations
 * @param {string} props.updateVariationsUrl - API endpoint for bulk variation updates
 *
 * @returns {JSX.Element} The rendered input/entry page
 */
export default function Input({
    session,
    shoppingurl,
    step,
    steps,
    product,
    heading,
    subheading,
    variationoptions,
    uploadExcel,
    saveUrl,
    createUrl,
    prevLink,
    nextLink,
    saveVariationUrl,
    updateVariationsUrl,
}) {

    // Initialize state with product data
    const [properties, setProperties] = useState(product.properties);
    const [variations, setVariations] = useState(variationoptions);
    const [variationerrors, setVariationErrors] = useState([]);
    const [noRows, setNoRows] = useState(false);
    const [warnings, setWarnings] = useState({});
    const [stepList, setStepList] = useState(steps);

    /**
     * Update properties with optional server persistence
     * Handles property updates and conditionally saves to server
     *
     * @param {Object} properties - Updated properties object
     * @param {boolean} doRequest - Whether to save to server (default: true)
     */
    function updateProps(properties, doRequest = true) {
        setProperties(properties);
        if (doRequest) {
            api.put(saveUrl, { properties: properties });
        }
    }

    /**
     * Handle previous step navigation
     * Navigates to previous step without validation
     */
    function prevClick() {
        Inertia.visit(prevLink);
    }

    /**
     * Handle next step navigation with validation
     * Validates all input data before allowing navigation
     */
    function nextClick() {
        if (validateInput()) {
            gotoNext();
        }
    }

    /**
     * Navigate to next step
     * Direct navigation without validation (used after successful validation)
     */
    function gotoNext() {
        Inertia.visit(nextLink);
    }

    /**
     * Handle step navigation with validation
     * Validates current data before allowing navigation to specified step
     *
     * @param {Event} e - Click event
     * @param {string} link - Target navigation URL
     */
    function clickStep(e, link) {
        e.preventDefault();
        if (validateInput()) {
            Inertia.visit(link);
        }
    }

		function onlyUnknowns() {
			const knownProducts = Object.values(product.products)
				.filter(item =>
					item &&
					![75515056, 75515066].includes(item.itemgroup)
				);

			return knownProducts.length === 0;
		}

    /**
     * Comprehensive validation for all input data
     * Validates variations array and individual variation properties
     * Checks for required fields and numeric constraints
     *
     * @returns {boolean} Whether validation passed
     */
    function validateInput() {
        let errorObject = {};
        let algemeenerror = [];

        // Check if variations list is empty
        if (variations.length <= 0) {
            errorObject = { 0: "NOROWS" };
            setNoRows(true);
        } else {
            // Validate each variation's properties
            let errors = variations.filter((variation, key) => {
                let props = variation.properties;

                // Check all required numeric fields
                if (
                    props.BREEDTE <= 0 ||
                    props.BREEDTE === "" ||
                    props.BREEDTE === null ||
                    isNaN(props.BREEDTE) ||
                    props.HOOGTE <= 0 ||
                    props.HOOGTE === "" ||
                    props.HOOGTE === null ||
                    isNaN(props.HOOGTE) ||
                    props.AANTAL <= 0 ||
                    props.AANTAL === "" ||
                    props.AANTAL === null ||
                    isNaN(props.AANTAL) ||
                    props.DIKTE <= 0 ||
                    props.DIKTE === "" ||
                    props.DIKTE === null ||
                    isNaN(props.DIKTE) <= 0 ||
                    // Core plate position validation (if core plate is used)
                    (properties.TL !== 0 &&
                        (props.TL_POS <= 0 ||
                            props.TL_POS === "" ||
                            props.TL_POS === null) <= 0)
                ) {
                    return variation;
                }
            });

            // Build error object with specific field errors for each variation
            if (errors.length > 0) {
                errorObject = Object.assign(
                    ...Object.entries(errors).map(([k, v]) => {
                        let array = [];
                        let props = v.properties;

                        // Check each field individually
                        if (
                            props.BREEDTE <= 0 ||
                            props.BREEDTE === "" ||
                            props.BREEDTE === null ||
                            isNaN(props.BREEDTE)
                        ) {
                            array.push("BREEDTE");
                        }
                        if (
                            props.HOOGTE <= 0 ||
                            props.HOOGTE === "" ||
                            props.HOOGTE === null ||
                            isNaN(props.HOOGTE)
                        ) {
                            array.push("HOOGTE");
                        }
                        if (
                            props.AANTAL <= 0 ||
                            props.AANTAL === "" ||
                            props.AANTAL === null ||
                            isNaN(props.AANTAL)
                        ) {
                            array.push("AANTAL");
                        }
                        if (
                            (
															  props.DIKTE <= 0 ||
                                props.DIKTE === "" ||
                                props.DIKTE === null ||
                                isNaN(props.DIKTE)
                            ) &&
	                          !onlyUnknowns()
                        ) {
                            if (!algemeenerror.includes("DIKTE")) {
                                algemeenerror.push("DIKTE");
                            }
                        }
                        if (
                            properties.TL !== 0 &&
                            (props.TL_POS <= 0 ||
                                props.TL_POS === "" ||
                                props.TL_POS === null)
                        ) {
                            if (!algemeenerror.includes("TL_POS")) {
                                algemeenerror.push("TL_POS");
                            }
                        }
                        return { [v.id]: array };
                    }),
                );
            }
        }

        errorObject.algemeen = algemeenerror;
        let errorobjectfilter = filterErrors(errorObject);
        setVariationErrors(errorobjectfilter);

        // Stop here if there are validation errors
        if (Object.keys(errorobjectfilter).length > 0) return false;

        // Check for size warnings (non-blocking)
		let warningMessages = [];
		for (const variationI in variations) {
			const variation = variations[variationI];
			const variationWidth = variation.properties.BREEDTE;
			const variationLength = variation.properties.HOOGTE;

			const layers = Object.values(variation.product.products).filter(item => item !== null);

			let layerWarnings = [];

			for (const layer of layers) {
				if (layer.ydimsize > 0 && layer.ydimsize < variationWidth) {
                    // adding the style tag in here is horrible, but for some reason there are global styles that I don't want to mess with
					layerWarnings.push(`<li style="font-size: 14px">Breedte van artikel '${layer.name}'</li>`);
				}
				if (layer.xdimsize > 0 && layer.xdimsize < variationLength) {
					layerWarnings.push(`<li style="font-size: 14px">Hoogte van artikel '${layer.name}'</li>`);
				}
			}

			if (layerWarnings.length > 0) {
				warningMessages.push(`Regel ${parseInt(variationI) + 1}:<ul>${layerWarnings.join('')}</ul>`)
			}
		}

		if (warningMessages.length > 0) warnings.sizes = warningMessages;

        // Pass validation if no warnings (or warnings are acceptable)
        return Object.keys(warnings).length <= 0;
    }

    /**
     * Filter out empty error arrays from error object
     * Cleans up error object by removing properties with empty arrays
     *
     * @param {Object} obj - Error object to filter
     * @returns {Object} Filtered error object
     */
    function filterErrors(obj) {
        for (let propName in obj) {
            if (
                obj.hasOwnProperty(propName) &&
                Array.isArray(obj[propName]) &&
                obj[propName].length <= 0
            ) {
                delete obj[propName];
            }
        }
        return obj;
    }

    /**
     * Handle shopping cart navigation with validation
     * Only allows navigation if current data is valid
     *
     * @returns {boolean} Whether navigation is allowed
     */
    function clickShopping() {
        return validateInput();
    }

    return (
        <Layout>
            <Sidepanel
                steps={stepList}
                step={step}
                validates
                product={product}
                clickStep={clickStep}
            />
            <Content
                steps={stepList}
                step={step}
                shoppingurl={shoppingurl}
                session={session}
                product={product}
                validates
                clickShopping={clickShopping}
            >
                <ContentOptions small>
                    <Heading heading={heading} subheading={subheading} />
                    <div className="question mb-3">
                        <div className="excelhide">
                            <p style={{ fontSize: 14, marginBottom: 3 }}>
                                Je kan de stuklijst ook vullen met een excel
                                sjabloon.
                            </p>
                            <a
                                href="/De_Mors_stuklijst_sjabloon.xlsx"
                                target="_blank"
                                download="De Mors stuklijst sjabloon"
                                style={{
                                    fontSize: 14,
                                    textDecoration: "underline",
                                    display: "inline-block",
                                    marginBottom: 25,
                                }}
                            >
                                Download stuklijst sjabloon
                            </a>
                        </div>
                        <Label label="Toelichting product" small />
                        <TextArea
                            properties={properties}
                            property="description"
                            updateProperties={updateProps}
                        />
                    </div>
                    <NavigationButtons
                        prevlink={prevClick}
                        nextlink={nextClick}
                        prevApi
                        nextApi
                    />
                </ContentOptions>
                <InputList
                    product={product}
                    variations={variations}
                    setVariations={setVariations}
                    saveVariationUrl={saveVariationUrl}
                    noRows={noRows}
                    setNoRows={setNoRows}
                    warnings={warnings}
                    setWarnings={setWarnings}
                    gotoNext={gotoNext}
                    uploadExcel={uploadExcel}
                    updateVariationsUrl={updateVariationsUrl}
                    createUrl={createUrl}
                    errors={variationerrors}
                />
            </Content>
        </Layout>
    );
}
